import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Facebook, Instagram } from 'lucide-react';

const AnimatedSection = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const About = () => {
  const [activeTab, setActiveTab] = useState('team'); // Default to team first

  const teamMembers = [
    {
      name: 'Richard Rozalewicz',
      role: 'Founder & Muay Thai Coach',
      bio: 'Driven by a passion for Combat Sports, Richard founded Cobra Thai to create a top-class training Academy. Richard has trained in combat sports for over 20 Years, Muay Thai being his go to Discipline.',
      image: '/site-assets/richard.jpg',
      instagram: 'https://instagram.com/rozzarichard',
    },
    {
      name: 'Dushan Burale',
      role: 'Head MMA Coach',
      bio: '3-Time National MMA Champion, Kickboxing National Championship, Professional Fighter – 31+ Combined Amateur & Pro Fights. A seasoned fighter and champion, Dushan leads the MMA programme at Cobra Thai.',
      image: '/site-assets/dushan.jpg',
      instagram: 'https://instagram.com/dushanburle',
    },
    {
      name: 'Prathamesh Deshpande',
      role: 'MMA Coach – Kickboxing, Boxing & MMA',
      bio: 'Asian MMA Championship Silver Medallist, Represented India at GAMMA World MMA Championship, Professional Fighter - 4-0-0. Prathamesh brings the grit and discipline of professional competition to his coaching.',
      image: 'https://static.wixstatic.com/media/7975cd_bdbb4ab7ff59408e970dd30725eed21b~mv2.jpeg/v1/fill/w_401,h_431,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202025-01-24%20at%202_44_51%20PM.jpeg',
      instagram: 'https://instagram.com/prathamesh_mma',
    },
    {
      name: 'Vijay Jagtap',
      role: 'MMA Coach – Kickboxing, Wrestling & MMA',
      bio: 'Amateur Fighter. Vijay\'s calm yet effective coaching style ensures every student develops a strong foundation in MMA while building confidence inside and outside the cage.',
      image: '/site-assets/viay.jpg',
      instagram: 'https://instagram.com/vijay.jagtap.054',
    },
    {
      name: 'Rizwan Shaikh',
      role: 'Kickboxing & Kids Coach',
      bio: 'A decorated champion with 9 titles including Inter-School, Pune, and 4x Maharashtra State Kickboxing Championships. With 10 years of training and 7 years coaching kids and adults, Rizwan specializes in technique development, competition prep, and building confidence & discipline in young fighters.',
      image: '/site-assets/rizwan.jpg',
    },
    {
      name: 'Akshay Shinde',
      role: 'MMA Coach, Amateur Fighter',
      bio: 'MMA record 12-02-0, 2 time Taekwondo National Gold, 2 time Kickboxing National Gold, Yongmoodo Black Belt, Taekwondo Black Belt.',
      image: '/site-assets/akshay.jpg',
      instagram: 'https://instagram.com/akshay_bodymindspirit',
    },
    {
      name: 'Ganesh Vinayak Rathod',
      role: 'MMA Coach – Wrestling Specialist',
      bio: 'Amateur Fighter, Silver Medallist in Maharashtra Kesari. With a strong wrestling pedigree, Ganesh integrates his expertise into MMA training, ensuring fighters excel in grappling and ground control.',
      image: '/site-assets/ganesh.jpg',
      instagram: 'https://instagram.com/paiganeshrathod',
    },
    {
      name: 'Abhay Maske',
      role: 'Striking Specialist',
      bio: 'State Gold Medalist in Kickboxing, Abhay masters the art of explosive power and precision. Trained under one of the sport\'s top coaches, he blends sharp technique with relentless drive.',
      image: '/site-assets/abhay.jpg',
      instagram: 'https://instagram.com/the_abhay_maske',
    },
    {
      name: 'Shantanu Kupte',
      role: 'Senior Front Desk Sales Associate',
      bio: 'Shantanu is the welcoming face of Cobra Thai. His enthusiasm and dedication ensure every member\'s experience starts and ends on a high note.',
      image: '/site-assets/shantanu.jpg',
      instagram: 'https://instagram.com/shantanu13_sk',
    },
    {
      name: 'Parvez Khan',
      role: 'Senior Housekeeping',
      bio: 'Parvez ensures that the gym remains pristine, creating a clean and welcoming environment for all members and staff.',
      image: '/site-assets/parvez.jpg',
    },
    {
      name: 'Sameer',
      role: 'Housekeeping',
      bio: 'Sameer keeps the training areas spotless and ready for action, ensuring members can focus on their workout in a clean, hygienic environment.',
      image: '/site-assets/sameer.jpg',
    },
  ];

  return (
    <div className="bg-cobra-black min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 md:px-8 lg:px-12 relative" data-testid="about-hero">
        <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-cobra-red/5 blur-[150px] rounded-full" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <AnimatedSection>
            <h1 className="font-anton text-5xl md:text-6xl lg:text-7xl text-white uppercase tracking-wider mb-4 text-center" data-testid="about-title">
              ABOUT <span className="text-cobra-red">COBRA THAI</span>
            </h1>
            <p className="font-manrope text-white/60 text-lg md:text-xl text-center max-w-2xl mx-auto">
              Pune's premier MMA & fitness community
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="px-4 md:px-8 lg:px-12 pb-8">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection delay={0.1}>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setActiveTab('team')}
                className={`px-8 py-3 font-anton text-lg uppercase tracking-wider rounded-sm transition-all duration-300 ${
                  activeTab === 'team'
                    ? 'bg-cobra-red text-white shadow-lg shadow-cobra-red/30'
                    : 'bg-cobra-dark/50 text-white/60 hover:text-white border border-white/10 hover:border-white/30'
                }`}
                data-testid="tab-team"
              >
                Meet The Team
              </button>
              <button
                onClick={() => setActiveTab('story')}
                className={`px-8 py-3 font-anton text-lg uppercase tracking-wider rounded-sm transition-all duration-300 ${
                  activeTab === 'story'
                    ? 'bg-cobra-red text-white shadow-lg shadow-cobra-red/30'
                    : 'bg-cobra-dark/50 text-white/60 hover:text-white border border-white/10 hover:border-white/30'
                }`}
                data-testid="tab-story"
              >
                Our Story
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Content Area */}
      <AnimatePresence mode="wait">
        {activeTab === 'team' ? (
          <motion.section
            key="team"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="py-12 md:py-20 px-4 md:px-8 lg:px-12 bg-gradient-to-b from-cobra-black via-cobra-dark to-cobra-black relative overflow-hidden"
            data-testid="team-section"
          >
            <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-cobra-red/5 blur-[120px] rounded-full" />
            <div className="max-w-7xl mx-auto relative z-10">
              <AnimatedSection>
                <h2 className="font-anton text-3xl md:text-4xl lg:text-5xl text-white uppercase tracking-wider mb-2" data-testid="team-title">
                  MEET THE <span className="text-cobra-red">TEAM</span>
                </h2>
                <p className="font-manrope text-white/60 text-lg mb-4">
                  Champions, coaches, and community builders
                </p>
                <div className="h-1 w-32 bg-cobra-red mb-12" />
              </AnimatedSection>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {teamMembers.map((member, index) => (
                  <AnimatedSection key={member.name} delay={index * 0.05}>
                    <motion.div
                      whileHover={{ y: -8 }}
                      className="bg-cobra-black/50 backdrop-blur-sm border border-white/10 hover:border-cobra-red/50 rounded-sm overflow-hidden group transition-all duration-500"
                      data-testid={`team-member-${index}`}
                    >
                      <div className="aspect-square overflow-hidden relative">
                        <motion.img
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.6 }}
                          src={member.image}
                          alt={member.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-cobra-black via-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                      <div className="p-5 relative">
                        <h3 className="font-anton text-xl text-white uppercase tracking-wide mb-1 group-hover:text-cobra-red transition-colors duration-300">
                          {member.name}
                        </h3>
                        <p className="text-cobra-red font-manrope text-sm mb-3 uppercase tracking-wide font-semibold">
                          {member.role}
                        </p>
                        {/* Bio with hover to expand */}
                        <div className="relative">
                          <p className="font-manrope text-white/70 text-sm leading-relaxed mb-3 line-clamp-2 group-hover:line-clamp-none transition-all duration-300">
                            {member.bio}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3">
                          {member.instagram && (
                            <a href={member.instagram} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-cobra-red transition-colors duration-300">
                              <Instagram size={16} />
                            </a>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </motion.section>
        ) : (
          <motion.section
            key="story"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="py-12 md:py-20 px-4 md:px-8 lg:px-12 relative"
            data-testid="our-story-section"
          >
            <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-cobra-red/5 blur-[150px] rounded-full" />
            
            <div className="max-w-4xl mx-auto relative z-10">
              <AnimatedSection>
                <h2 className="font-anton text-4xl md:text-5xl text-white uppercase tracking-wider mb-8" data-testid="story-title">
                  OUR <span className="text-cobra-red">STORY</span>
                </h2>
                
                <div className="space-y-6 font-manrope text-white/80 text-base md:text-lg leading-relaxed">
                  <p className="text-xl md:text-2xl text-white font-light">
                    When Richard moved from the UK to Pune, he had one problem – he couldn't find a real, legitimate martial arts academy anywhere near him.
                  </p>
                  
                  <p>
                    So he built one.
                  </p>
                  
                  <p>
                    <strong className="text-white">Cobra Thai</strong> was born in Kharadi, Pune – not as just another gym, but as an answer to a gap in the market. A place where serious training meets a welcoming community. Where beginners stand alongside professional fighters. Where technique is taught by coaches who've actually competed at the highest levels.
                  </p>
                  
                  <p>
                    Today, we're recognised as one of <strong className="text-white">India's premier MMA facilities</strong>, offering top-tier instruction in Brazilian Jiu-Jitsu, Muay Thai, MMA, Wrestling, Boxing, and Kickboxing. Our members range from complete beginners looking to get fit, to competitive fighters preparing for their next bout.
                  </p>
                  
                  <p>
                    What makes us different? <strong className="text-white">Our coaches have been there.</strong> National champions, international competitors, professional fighters – they don't just teach techniques, they've used them in real competition. That experience translates into training that actually works.
                  </p>
                  
                  <p>
                    We welcome everyone from age 7 to 65+. Whether you're looking to learn self-defence, transform your fitness, or compete in combat sports – this is where your journey begins.
                  </p>
                </div>
                
                {/* Switch to Team button */}
                <motion.button
                  onClick={() => setActiveTab('team')}
                  whileHover={{ scale: 1.05 }}
                  className="mt-10 inline-flex items-center gap-2 bg-cobra-red hover:bg-red-700 text-white font-bold uppercase tracking-wider px-6 py-3 rounded-sm transition-all"
                >
                  <span>Meet Our Team</span>
                </motion.button>
              </AnimatedSection>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default About;
